import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import OnboardingLayout from '../../components/onboarding/OnboardingLayout';
import apiClient from '../../services/api/apiClient';

function Step5ReferenceAccounts() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editAccountId = searchParams.get('edit');
  const isEditMode = !!editAccountId;

  const [accounts, setAccounts] = useState(['', '', '']);
  const [validating, setValidating] = useState([false, false, false]);
  const [loading, setLoading] = useState(isEditMode);

  useEffect(() => {
    if (isEditMode && editAccountId) {
      loadAccountSettings(editAccountId);
    }
  }, [isEditMode, editAccountId]);

  const loadAccountSettings = async (accountId) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/user/social-accounts/account-settings/id/${accountId}`);
      if (response.data.success && response.data.data.settings?.reference_accounts) {
        const refAccounts = response.data.data.settings.reference_accounts;
        const filledAccounts = [...refAccounts, '', '', ''].slice(0, 3);
        setAccounts(filledAccounts);
      }
    } catch (error) {
      console.error('載入設定失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccountChange = (index, value) => {
    const newAccounts = [...accounts];
    newAccounts[index] = value;
    setAccounts(newAccounts);
  };

  const handleValidateAccount = async (index) => {
    const account = accounts[index].trim();
    if (!account) return;

    const newValidating = [...validating];
    newValidating[index] = true;
    setValidating(newValidating);

    // Simulate validation (replace with actual API call)
    setTimeout(() => {
      newValidating[index] = false;
      setValidating(newValidating);
    }, 1000);
  };

  const handleNext = () => {
    const validAccounts = accounts.filter(acc => acc.trim() !== '');
    if (validAccounts.length > 0) {
      localStorage.setItem('onboarding_reference_accounts', JSON.stringify(validAccounts));
      if (isEditMode && editAccountId) {
        navigate(`/onboarding/step6?edit=${editAccountId}`);
      } else {
        navigate('/onboarding/step6');
      }
    }
  };

  const handleBack = () => {
    if (isEditMode && editAccountId) {
      navigate(`/onboarding/step4?edit=${editAccountId}`);
    } else {
      navigate('/onboarding/step4');
    }
  };

  const handleSkip = () => {
    localStorage.setItem('onboarding_reference_accounts', JSON.stringify([]));
    if (isEditMode && editAccountId) {
      navigate(`/onboarding/step6?edit=${editAccountId}`);
    } else {
      navigate('/onboarding/step6');
    }
  };

  const illustration = (
    <div className="w-full max-w-md mx-auto">
      <div className="relative flex items-center justify-center">
        {/* User profiles grid */}
        <div className="relative">
          {/* Background circle */}
          <div className="w-64 h-64 rounded-full bg-white/60 backdrop-blur-sm shadow-lg flex items-center justify-center">
            <div className="grid grid-cols-2 gap-4">
              {/* Profile 1 - With star */}
              <div className="relative">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-400 to-blue-500 shadow-md flex items-center justify-center">
                  <Icon icon="mdi:account" className="text-4xl text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 to-orange-400 shadow-md flex items-center justify-center">
                  <Icon icon="mdi:star" className="text-sm text-white" />
                </div>
              </div>

              {/* Profile 2 */}
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-400 to-violet-400 shadow-md flex items-center justify-center">
                <Icon icon="mdi:account" className="text-4xl text-white" />
              </div>

              {/* Profile 3 */}
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-pink-400 to-rose-400 shadow-md flex items-center justify-center">
                <Icon icon="mdi:account" className="text-4xl text-white" />
              </div>

              {/* Add button */}
              <div className="w-20 h-20 rounded-2xl bg-white/80 shadow-md flex items-center justify-center border-2 border-dashed border-blue-300">
                <Icon icon="mdi:plus" className="text-4xl text-blue-400" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const hasValidAccounts = accounts.some(acc => acc.trim() !== '');

  return (
    <OnboardingLayout
      currentStep={5}
      totalSteps={6}
      title="有沒有欣賞的典範帳號？"
      illustration={illustration}
      onBack={handleBack}
      progressColor="#60A5FA"
    >
      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <Icon icon="mdi:information-outline" className="text-blue-500 text-xl flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700">
            <p className="font-medium mb-1">學習參考</p>
            <p className="text-gray-600">
              填入 1-3 個你欣賞的 Threads 帳號（例如：@username），AI 會分析這些帳號的風格與策略，作為你的內容創作參考。
            </p>
          </div>
        </div>
      </div>

      {/* Account Input Fields */}
      <div className="space-y-4 mb-6">
        {accounts.map((account, index) => (
          <div key={index} className="flex items-center gap-3">
            <div className="flex-1 relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                <Icon icon="mdi:at" className="text-xl" />
              </div>
              <input
                type="text"
                value={account}
                onChange={(e) => handleAccountChange(index, e.target.value)}
                onBlur={() => handleValidateAccount(index)}
                placeholder={`典範帳號 ${index + 1} ${index === 0 ? '(必填)' : '(選填)'}`}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
              />
            </div>
            {validating[index] && (
              <Icon icon="line-md:loading-loop" className="text-xl text-blue-500" />
            )}
            {account.trim() !== '' && !validating[index] && (
              <Icon icon="mdi:check-circle" className="text-xl text-green-500" />
            )}
          </div>
        ))}
      </div>

      {/* Examples */}
      <div className="bg-gray-50 rounded-xl p-4 mb-6">
        <p className="text-xs font-medium text-gray-500 mb-3">熱門參考帳號範例：</p>
        <div className="flex flex-wrap gap-2">
          {['@garyvee', '@neil_patel', '@hubspot', '@semrush'].map((example, index) => (
            <button
              key={index}
              onClick={() => handleAccountChange(0, example)}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
            >
              {example}
            </button>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between mt-8">
        <button
          onClick={handleSkip}
          className="px-6 py-2.5 text-gray-600 font-medium rounded-xl hover:bg-gray-100 transition-colors"
        >
          跳過此步驟
        </button>
        <button
          onClick={handleNext}
          disabled={!hasValidAccounts}
          className={`
            px-8 py-3 font-medium rounded-xl transition-colors flex items-center gap-2
            ${hasValidAccounts
              ? 'bg-blue-500 text-white hover:bg-blue-600'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          Next
          <Icon icon="mdi:arrow-right" className="text-xl" />
        </button>
      </div>
    </OnboardingLayout>
  );
}

export default Step5ReferenceAccounts;
